import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { authenticateUser } from '@/services/steemit/authService'
import { getUserRC } from '@/services/steemit/rcService'
import { authStorage } from '@/services/storage/authStorage'

interface User {
  username: string
  postingKey: string
  rcPercentage: number
}

interface AuthState {
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  login: (username: string, postingKey: string) => Promise<void>
  logout: () => void
  updateRcPercentage: (percentage: number) => void
  refreshRC: () => Promise<void>
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,

      login: async (username: string, postingKey: string) => {
        set({ isLoading: true, error: null })

        try {
          const result = await authenticateUser(username, postingKey)

          if (!result.success) {
            set({ isLoading: false, error: result.error || 'Authentication failed' })
            return
          }

          const user: User = {
            username,
            postingKey,
            rcPercentage: result.rcPercentage || 100,
          }

          // Save to localStorage
          authStorage.saveAuth(user)

          set({
            user,
            isAuthenticated: true,
            isLoading: false,
            error: null,
          })
        } catch (error) {
          set({
            isLoading: false,
            error: error instanceof Error ? error.message : 'Authentication failed',
          })
        }
      },

      logout: () => {
        authStorage.clearAuth()
        set({
          user: null,
          isAuthenticated: false,
          error: null,
        })
      },

      updateRcPercentage: (percentage: number) => {
        set((state) => ({
          user: state.user ? { ...state.user, rcPercentage: percentage } : null,
        }))
      },

      refreshRC: async () => {
        const { user } = get()
        if (!user) return

        try {
          const rc = await getUserRC(user.username)
          set((state) => ({
            user: state.user ? { ...state.user, rcPercentage: rc.percentage } : null,
          }))
        } catch (error) {
          console.error('Failed to refresh RC:', error)
        }
      },
    }),
    {
      name: 'fast-steemit-auth',
      partialize: (state) => ({
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
)
